import { hasAnyOrgRole } from '@/helpers/roles';
import { useAppStore } from '@/stores/app';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { Role } from '@/types';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStatus } from './useAuthStatus';

/**
 * Hook to protect routes by redirecting users when they lose access
 * due to role or organization changes.
 *
 * This hook monitors the user's current role and selected organization.
 * If the user switches to a role that doesn't have access to the current route,
 * or switches to an organization where they don't have the required role,
 * they will be automatically redirected.
 *
 * @param requiredRoles - Array of roles required to access the route
 * @param redirectTo - Path to redirect to if access is lost (default: '/unauthorized')
 *
 * @example
 * ```tsx
 * import { useRouteProtection } from '@/hooks';
 * import { ROLES } from '@/types';
 *
 * function UsersPage() {
 *   // Protect this route - will redirect if role changes
 *   useRouteProtection([ROLES[0]]); // ROLES[0] = 'super_user'
 *
 *   // rest of component
 * }
 * ```
 */
export function useRouteProtection(requiredRoles: Role[], redirectTo = '/unauthorized') {
  const navigate = useNavigate();
  const { user } = useAuthStatus();
  // selectedOrganization is not persisted; fall back to selectedOrgStub (persisted) on hard
  // refresh so the hook doesn't redirect before the full org object is restored.
  const effectiveOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));

  useEffect(() => {
    const run = async () => {
      // Skip if no user (will be handled by root route auth check)
      if (!user) {
        return;
      }

      const redirectWithReferrer = async () => {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('unauthorized_referrer', window.location.pathname);
        }
        try {
          useUnauthorizedReferrerStore.getState().setReferrerPath(window.location.pathname);
        } catch {}
        // Avoid re-navigating if already on the destination.
        if (typeof window === 'undefined' || window.location.pathname !== redirectTo) {
          await navigate({ to: redirectTo });
        }
      };

      // If no organization is selected, redirect
      if (!effectiveOrg?.id) {
        await redirectWithReferrer();
        return;
      }

      // Check if user still has required role in selected org
      const hasAccess = hasAnyOrgRole(user.assumed_roles, effectiveOrg.id, requiredRoles);

      if (!hasAccess) {
        await redirectWithReferrer();
      }
    };
    void run();
  }, [user, effectiveOrg, requiredRoles, redirectTo, navigate]);
}
